import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { useAuth } from "context/AuthContext";
import api from "services/api";
import { isAdminUser } from "modules/systems/services/application.service";
import useSubcontractPermissions, { SUBCONTRACT_PERMISSIONS } from "../hooks/useSubcontractPermissions";
import ApprovalEditSubmitView from "./ApprovalEditSubmitView";
import ApprovalReviewView from "./ApprovalReviewView";
import ApprovalWorkflowView from "./ApprovalWorkflowView";
import AppSnackbar from "components/common/snackbar/AppSnackbar";

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("vi-VN")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u0111/g, "d")
    .replace(/đ/g, "d");

const getValue = (source, key) => source?.[key] ?? source?.[`${key[0].toUpperCase()}${key.slice(1)}`];
const getUserId = (user) => Number(user?.userId ?? user?.UserId);
const getSubmittedUserId = (profile) => Number(getValue(profile, "submittedUserId"));
const getApprovalSteps = (profile) => getValue(profile, "steps") || [];

const isPendingApprovalStatus = (status) => {
  const value = normalizeStatus(status);
  return value.includes("cho") && value.includes("duyet");
};

const isEditingStatus = (status) => {
  const value = normalizeStatus(status);
  return value.includes("chinh sua") || value.includes("dang sua") || value.includes("yeu cau sua");
};

const isApprovedStatus = (status) => {
  const value = normalizeStatus(status);
  return value.includes("da phe duyet") || value.includes("duoc phe duyet");
};

const getLatestApprovalProfile = (approvals = []) =>
  [...approvals].sort((left, right) => {
    const leftCount = Number(getValue(left, "submitCount") || 0);
    const rightCount = Number(getValue(right, "submitCount") || 0);
    if (rightCount !== leftCount) return rightCount - leftCount;

    const leftTime = new Date(getValue(left, "submittedAt") || getValue(left, "createdAt") || 0).getTime();
    const rightTime = new Date(getValue(right, "submittedAt") || getValue(right, "createdAt") || 0).getTime();
    return rightTime - leftTime;
  })[0] || null;

const getCurrentApprovalStep = (profile) => {
  const steps = getApprovalSteps(profile);
  const currentStepOrder = Number(getValue(profile, "currentStepOrder"));
  return steps.find((step) => Number(getValue(step, "stepOrder")) === currentStepOrder)
    || steps.find((step) => {
      const status = normalizeStatus(getValue(step, "status"));
      return status.includes("dang xu ly");
    })
    || null;
};

export default function ApprovalsTab(props) {
  const { subcontractId, subcontract = {}, approvals = [] } = props;
  const { user } = useAuth();
  const permissions = useSubcontractPermissions();
  const [accessState, setAccessState] = useState({ loading: true, hasAccess: false, error: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    let active = true;

    const checkAccess = async () => {
      if (!user?.userId) {
        if (active) setAccessState({ loading: false, hasAccess: false, error: "Không thể xác định người dùng." });
        return;
      }

      if (isAdminUser(user)) {
        if (active) setAccessState({ loading: false, hasAccess: true, error: "" });
        return;
      }

      try {
        const response = await api.get(`/SubcontractAccess/check-subcontract-access/${subcontractId}`);
        if (active) {
          setAccessState({
            loading: false,
            hasAccess: Boolean(response?.data?.hasAccess),
            error: response?.data?.hasAccess ? "" : "Bạn không được phân quyền xem quyết định giao khoán này.",
          });
        }
      } catch (error) {
        if (active) {
          setAccessState({
            loading: false,
            hasAccess: false,
            error: error?.response?.data?.message || error.message || "Không thể kiểm tra quyền truy cập giao khoán.",
          });
        }
      }
    };

    setAccessState({ loading: true, hasAccess: false, error: "" });
    checkAccess();

    return () => {
      active = false;
    };
  }, [subcontractId, user]);

  const mode = useMemo(() => {
    if (permissions.loading || accessState.loading || !accessState.hasAccess || !user?.userId) return "view";

    const userId = getUserId(user);
    const profile = getLatestApprovalProfile(approvals);
    const profileStatus = getValue(profile, "status") || getValue(subcontract, "approvalStatus") || getValue(subcontract, "status");
    const currentStep = getCurrentApprovalStep(profile);
    const currentApproverId = Number(getValue(currentStep, "approverUserId") || getValue(profile, "approverUserId"));
    const hasApprovePermission = permissions.has(SUBCONTRACT_PERMISSIONS.approvalApprove);
    const hasSubmitPermission = permissions.has(SUBCONTRACT_PERMISSIONS.approvalSubmit);

    if (
      profile &&
      hasApprovePermission &&
      isPendingApprovalStatus(profileStatus) &&
      currentApproverId === userId
    ) {
      return "approve";
    }

    const submittedUserId = getSubmittedUserId(profile);
    const canCreateFirstSubmission = !profile && hasSubmitPermission;
    const canEditExistingDraft = Boolean(profile) &&
      hasSubmitPermission &&
      submittedUserId === userId &&
      (isEditingStatus(profileStatus) || (!isPendingApprovalStatus(profileStatus) && !isApprovedStatus(profileStatus)));

    if ((canCreateFirstSubmission || canEditExistingDraft) && !isApprovedStatus(profileStatus) && !isPendingApprovalStatus(profileStatus)) {
      return "edit";
    }

    return "view";
  }, [accessState, approvals, permissions, subcontract, user]);

  if (permissions.loading || accessState.loading) {
    return <Box sx={{ display: "grid", placeItems: "center", minHeight: 240 }}><CircularProgress /></Box>;
  }

  if (!accessState.hasAccess) {
    return <Alert severity="error">{accessState.error || "Bạn không có quyền truy cập giao khoán này."}</Alert>;
  }

  const onNotify = (message, severity = "info") => setSnackbar({ open: true, message, severity });
  return (
    <>
      {mode === "approve" ? <ApprovalReviewView {...props} currentUser={user} onNotify={onNotify} />
        : mode === "edit" ? <ApprovalEditSubmitView {...props} currentUser={user} onNotify={onNotify} />
          : <ApprovalWorkflowView {...props} mode="view" currentUser={user} onNotify={onNotify} />}
      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </>
  );
}
